import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectFeaturedItems } from '../features/navDataSlice';
import { useCurrentPath } from '../context/CurrentPathContext';
import useFeaturedOverflow from '../hooks/useFeaturedOverflow';
import useFeaturedMegaMenuState from '../hooks/useFeaturedMegaMenuState';
import useRefMap from '../hooks/useRefMap';
import { hasActiveDescendant, isNavItemActive } from '../utils/navItemUtils';
import FeaturedNavItem from './FeaturedNavItem';
import MegaMenu from './MegaMenu';

const isHoverCapable = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(hover: hover)').matches;

/**
 * True when a featured item's trigger should render active: its own mega
 * menu is open, the current page matches the item itself (plain links), or
 * matches one of its descendants (mega menu items, via hasActiveDescendant).
 *
 * @param {NavigationItem} item
 * @param {{ openMegaMenuId: string|null, currentPath: string, isMegaMenuItem: boolean }} context
 * @returns {boolean}
 */
const getIsActive = (item, { openMegaMenuId, currentPath, isMegaMenuItem }) => {
	if (openMegaMenuId === item.id) return true;
	return isMegaMenuItem
		? hasActiveDescendant(item, currentPath)
		: isNavItemActive(item, currentPath);
};

const FeaturedNavBar = ({ closeMegaMenuRef }) => {
	const featuredItems = useSelector(selectFeaturedItems);
	const currentPath = useCurrentPath();
	const containerRef = useRef(null);
	const visibleCount = useFeaturedOverflow(
		containerRef,
		featuredItems.length
	);
	const triggerRefs = useRefMap(featuredItems.map((item) => item.id));
	const hoverCapable = useMemo(() => isHoverCapable(), []);

	const {
		openMegaMenuId,
		megaMenuItems,
		hoverIntent,
		handleClose,
		handleItemClick,
		handleItemHoverEnter,
		handleItemHoverFocus
	} = useFeaturedMegaMenuState(featuredItems, closeMegaMenuRef);

	if (featuredItems.length === 0) return null;

	return (
		<nav
			aria-label="Main navigation"
			className="ws-nav-drawer__featured-bar-wrapper"
		>
			<div
				ref={containerRef}
				className="ws-nav-drawer__featured-bar d-flex align-items-center overflow-hidden"
			>
				{featuredItems.slice(0, visibleCount).map((item) => {
					const isMegaMenuItem = item.columns?.length > 0;
					const isActive = getIsActive(item, {
						openMegaMenuId,
						currentPath,
						isMegaMenuItem
					});
					// Only mega menu items get hover/focus-intent wiring — plain
					// links have nothing to open on hover.
					const hoverHandlers = isMegaMenuItem
						? {
								onMouseEnter: hoverCapable
									? () => handleItemHoverEnter(item)
									: undefined,
								onMouseLeave: hoverCapable
									? hoverIntent.onMouseLeave
									: undefined,
								onFocus: () => handleItemHoverFocus(item),
								onBlur: hoverIntent.onBlur
						  }
						: {};

					return (
						<FeaturedNavItem
							key={item.id}
							ref={triggerRefs.current[item.id]}
							item={item}
							isActive={isActive}
							isMegaMenuOpen={openMegaMenuId === item.id}
							onClick={handleItemClick}
							{...hoverHandlers}
						/>
					);
				})}
			</div>
			{megaMenuItems.map((item) => (
				<MegaMenu
					key={item.id}
					item={item}
					visible={openMegaMenuId === item.id}
					onClose={handleClose}
					triggerRef={triggerRefs.current[item.id]}
				/>
			))}
		</nav>
	);
};

FeaturedNavBar.propTypes = {
	closeMegaMenuRef: PropTypes.shape({ current: PropTypes.func })
};

FeaturedNavBar.defaultProps = {
	closeMegaMenuRef: null
};

export default FeaturedNavBar;
